import type { Session } from "@/services/api.service";

/*
 * The Conversations list can be ordered, and date-filtered, by two times:
 *   saved   — when the tutor saved the slide's row (the studio's created_at).
 *   started — when the lesson session was first started. A resumed lesson
 *             keeps this from its first attempt, so it can be months old.
 */
export type ChatHistoryOrder = "saved" | "started";

export const CHAT_HISTORY_ORDER_LABELS: ReadonlyArray<{ value: ChatHistoryOrder; label: string }> = [
  { value: "saved", label: "Last saved" },
  { value: "started", label: "Session start" },
];

export const isChatHistoryOrder = (value: string): value is ChatHistoryOrder =>
  value === "saved" || value === "started";

// A key time is epoch milliseconds (13 digits); some old rows hold seconds (10).
const fromKeyTime = (value: string | undefined): Date | null => {
  if (!value || !/^\d{10}(\d{3})?$/.test(value)) return null;
  const date = new Date(Number(value) * (value.length === 10 ? 1000 : 1));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** The time a session is listed by in this order; null when the row does not carry it. */
export const sessionTime = (session: Session, order: ChatHistoryOrder): Date | null => {
  // session_key is "<session start ms>#<slide>#<save ms>".
  const [startMs, , savedMs] = session.session_key.split("#");
  if (order === "started") return fromKeyTime(startMs);
  if (session.created_at) {
    const saved = new Date(session.created_at);
    if (!Number.isNaN(saved.getTime())) return saved;
  }
  return fromKeyTime(savedMs);
};
